import { useState, useEffect } from "react";
import { Card, CardHeader, CardTitle, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { api } from "../../api";
import { useAuth } from "../../context";
import {
  Plus,
  Trash2,
  Loader2,
  Users,
  AlertTriangle,
  Copy,
  Check,
} from "lucide-react";

interface AdminWallet {
  id: number;
  address: string;
  label: string | null;
  isActive: boolean;
  createdAt: string | null;
}

export function AdminWalletsPage() {
  const { user } = useAuth();
  const [wallets, setWallets] = useState<AdminWallet[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [newWallet, setNewWallet] = useState("");
  const [newLabel, setNewLabel] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [copiedAddress, setCopiedAddress] = useState<string | null>(null);

  useEffect(() => {
    fetchWallets();
  }, []);

  async function fetchWallets() {
    try {
      const response = await api.admin.wallets.get();
      if (response.data && "data" in response.data) {
        setWallets(response.data.data as AdminWallet[]);
      }
    } catch (err) {
      setError("Failed to load admin wallets");
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }

  const handleAddWallet = async () => {
    if (!newWallet.trim()) {
      setError("Wallet address is required");
      return;
    }

    setIsSubmitting(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const response = await api.admin.wallets.post({
        wallet: newWallet.trim(),
        label: newLabel.trim() || undefined,
      });

      if (response.data && "success" in response.data && response.data.success) {
        setSuccessMessage(response.data.message as string);
        setNewWallet("");
        setNewLabel("");
        setIsAdding(false);
        fetchWallets();
      } else if (response.data && "error" in response.data) {
        setError(response.data.error as string);
      }
    } catch (err) {
      setError("Failed to add admin wallet");
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRemoveWallet = async (wallet: AdminWallet) => {
    if (wallet.address === user?.wallet) {
      setError("Cannot remove your own admin access");
      return;
    }

    setDeletingId(wallet.id);
    setError(null);
    setSuccessMessage(null);

    try {
      const response = await api.admin.wallets({ wallet: wallet.address }).delete();

      if (response.data && "success" in response.data && response.data.success) {
        setSuccessMessage(response.data.message as string);
        fetchWallets();
      } else if (response.data && "error" in response.data) {
        setError(response.data.error as string);
      }
    } catch (err) {
      setError("Failed to remove admin wallet");
      console.error(err);
    } finally {
      setDeletingId(null);
    }
  };

  const copyToClipboard = async (address: string) => {
    await navigator.clipboard.writeText(address);
    setCopiedAddress(address);
    setTimeout(() => setCopiedAddress(null), 2000);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const activeWallets = wallets.filter((w) => w.isActive);
  const inactiveWallets = wallets.filter((w) => !w.isActive);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Admin Wallets</h1>
          <p className="text-muted-foreground mt-1">
            Manage authorized admin wallet addresses
          </p>
        </div>
        <Button onClick={() => setIsAdding(true)} disabled={isAdding}>
          <Plus className="w-4 h-4 mr-2" />
          Add Wallet
        </Button>
      </div>

      {error && (
        <div className="bg-destructive/10 border border-destructive/20 rounded-md p-4 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-destructive" />
          <p className="text-destructive">{error}</p>
        </div>
      )}

      {successMessage && (
        <div className="bg-green-500/10 border border-green-500/20 rounded-md p-4">
          <p className="text-green-400">{successMessage}</p>
        </div>
      )}

      {/* Add Wallet Form */}
      {isAdding && (
        <Card>
          <CardHeader>
            <CardTitle>Add New Admin Wallet</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <label className="text-sm text-muted-foreground">
                  Wallet Address
                </label>
                <input
                  type="text"
                  value={newWallet}
                  onChange={(e) => setNewWallet(e.target.value)}
                  className="w-full mt-1 px-3 py-2 bg-background border border-border rounded-md font-mono text-sm"
                  placeholder="Enter Solana wallet address"
                />
              </div>
              <div>
                <label className="text-sm text-muted-foreground">
                  Label (optional)
                </label>
                <input
                  type="text"
                  value={newLabel}
                  onChange={(e) => setNewLabel(e.target.value)}
                  className="w-full mt-1 px-3 py-2 bg-background border border-border rounded-md text-sm"
                  placeholder="e.g., Team Member Name"
                />
              </div>
              <div className="flex gap-2">
                <Button onClick={handleAddWallet} disabled={isSubmitting}>
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Adding...
                    </>
                  ) : (
                    "Add Wallet"
                  )}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setIsAdding(false);
                    setNewWallet("");
                    setNewLabel("");
                  }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Active Wallets */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5" />
            Active Admins ({activeWallets.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {activeWallets.length === 0 ? (
            <p className="text-muted-foreground text-center py-4">
              No active admin wallets
            </p>
          ) : (
            <div className="divide-y divide-border">
              {activeWallets.map((wallet) => (
                <div
                  key={wallet.id}
                  className="flex items-center justify-between py-4"
                >
                  <div className="flex items-center gap-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-sm">
                          {wallet.address.slice(0, 8)}...
                          {wallet.address.slice(-8)}
                        </span>
                        <button
                          onClick={() => copyToClipboard(wallet.address)}
                          className="text-muted-foreground hover:text-foreground"
                        >
                          {copiedAddress === wallet.address ? (
                            <Check className="w-4 h-4 text-green-500" />
                          ) : (
                            <Copy className="w-4 h-4" />
                          )}
                        </button>
                        {wallet.address === user?.wallet && (
                          <Badge variant="outline">You</Badge>
                        )}
                      </div>
                      {wallet.label && (
                        <p className="text-sm text-muted-foreground mt-1">
                          {wallet.label}
                        </p>
                      )}
                      {wallet.createdAt && (
                        <p className="text-xs text-muted-foreground">
                          Added{" "}
                          {new Date(wallet.createdAt).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                  </div>
                  {wallet.address !== user?.wallet && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveWallet(wallet)}
                      disabled={deletingId === wallet.id}
                      className="text-destructive hover:text-destructive"
                    >
                      {deletingId === wallet.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Trash2 className="w-4 h-4" />
                      )}
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Inactive Wallets */}
      {inactiveWallets.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-muted-foreground">
              Inactive Wallets ({inactiveWallets.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="divide-y divide-border">
              {inactiveWallets.map((wallet) => (
                <div
                  key={wallet.id}
                  className="flex items-center justify-between py-4 opacity-50"
                >
                  <div>
                    <span className="font-mono text-sm">
                      {wallet.address.slice(0, 8)}...{wallet.address.slice(-8)}
                    </span>
                    {wallet.label && (
                      <p className="text-sm text-muted-foreground mt-1">
                        {wallet.label}
                      </p>
                    )}
                  </div>
                  <Badge variant="outline">Removed</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
